import { useState } from 'react';
import {
  List, Create, Edit, SimpleForm, Toolbar, SaveButton, TextInput, NumberInput, SelectInput, BooleanInput, DateTimeInput,
  FormDataConsumer, required, useListContext, useRedirect,
} from 'react-admin';
import {
  Box, Typography, TextField, Stack, CircularProgress, InputAdornment, FormControlLabel, Switch,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { tbl, inputSx, layout, badgeStyle, clickableA11y, WEBSITE_COUPON_KIND, WEBSITE_COUPON_ELIGIBILITY } from '../styles/ds';
import { fmtUsd } from './websiteApi';

/*
 * Website coupons (WD-006; owner decisions 2026-11-04). One coupon per order.
 * Percent / fixed amount come off the products only, never the shipping; free
 * shipping is $0 shipping even above the shipping ranges. The code is private —
 * the customer sees only the description. "Shluchim only" works for website
 * accounts linked to a CRM customer marked eligible on the customer's page.
 * Dates and limits are checked when the order is submitted; a cancelled or
 * rejected order (before payment) gives its use back. No delete: deactivate.
 */

const fmtDate = d => (d ? new Date(d).toLocaleDateString('he-IL') : '—');
const benefit = c => (c.kind === 'percent' ? `${c.percent}%` : c.kind === 'fixed' ? fmtUsd(c.amount_minor) : 'משלוח חינם');

const Filters = () => {
  const { filterValues, setFilters } = useListContext();
  const [search, setSearch] = useState(filterValues?.q || '');
  return (
    <Stack className="crm-wrap crm-filter-row" direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap alignItems="center">
      <TextField size="small" placeholder="חיפוש לפי קוד או תיאור..." aria-label="חיפוש קופונים"
        value={search} onChange={e => { setSearch(e.target.value); setFilters({ ...filterValues, q: e.target.value || undefined }); }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
          sx: { ...inputSx, direction: 'rtl' },
        }}
        sx={{ width: 300, maxWidth: '100%' }} />
      <FormControlLabel label="פעילים בלבד" control={
        <Switch checked={!!filterValues?.active} onChange={e => setFilters({ ...filterValues, active: e.target.checked || undefined })} />
      } />
    </Stack>
  );
};

const CouponsTable = () => {
  const { data = [], isPending } = useListContext();
  const redirect = useRedirect();
  if (isPending) return <Box p={4} textAlign="center"><CircularProgress /></Box>;
  if (!data.length) return <Typography p={3} color="text.secondary">אין קופונים להצגה.</Typography>;
  return (
    <TableContainer component={Box} sx={tbl.container}>
      <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="קופונים"><Table>
        <TableHead><TableRow>
          <TableCell>קוד</TableCell>
          <TableCell>סוג</TableCell>
          <TableCell align="center">הטבה</TableCell>
          <TableCell>זכאות</TableCell>
          <TableCell align="center">תוקף</TableCell>
          <TableCell align="center">שימושים</TableCell>
          <TableCell align="center">פעיל</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {data.map((c, i) => (
            <TableRow key={c.id} {...clickableA11y(() => redirect('edit', 'website_coupons', c.id), { ariaLabel: `קופון ${c.code}` })}
              onClick={() => redirect('edit', 'website_coupons', c.id)} sx={tbl.bodyRow(i)}>
              <TableCell>
                <Typography fontSize={14} fontWeight={700} dir="ltr" textAlign="right">{c.code}</Typography>
                <Typography fontSize={12} color="text.secondary" dir="ltr" textAlign="right">{c.label}</Typography>
              </TableCell>
              <TableCell><span style={badgeStyle(WEBSITE_COUPON_KIND, c.kind)}>{WEBSITE_COUPON_KIND[c.kind]?.label}</span></TableCell>
              <TableCell align="center" dir="ltr">
                {benefit(c)}
                {c.min_subtotal_minor !== null && <Typography fontSize={12} color="text.secondary">מעל {fmtUsd(c.min_subtotal_minor)}</Typography>}
              </TableCell>
              <TableCell>{WEBSITE_COUPON_ELIGIBILITY[c.eligibility]?.label}</TableCell>
              <TableCell align="center">{c.starts_at || c.ends_at ? `${fmtDate(c.starts_at)} – ${fmtDate(c.ends_at)}` : 'ללא הגבלה'}</TableCell>
              <TableCell align="center">{c.redemptions}{c.max_redemptions ? ` / ${c.max_redemptions}` : ''}</TableCell>
              <TableCell align="center">{c.active ? 'כן' : 'לא'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></Box>
    </TableContainer>
  );
};

export const WebsiteCouponList = () => (
  <List title="קופונים" storeKey={false} sort={{ field: 'created_at', order: 'DESC' }} sx={tbl.listSx} className="crm-mobile-list crm-inline-list">
    <Typography variant="body2" color="text.secondary" mb={2}>
      קופון אחד להזמנה. הקוד פרטי — באתר הלקוח רואה רק את התיאור. "שימושים" סופרים הזמנות שנשלחו; הזמנה שבוטלה או נדחתה לפני תשלום מחזירה את השימוש.
    </Typography>
    <Filters />
    <CouponsTable />
  </List>
);

// Dollars in the form, cents in the API.
const usdInput = { format: v => (v === null || v === undefined ? '' : v / 100), parse: v => (v === null || v === '' ? null : Math.round(Number(v) * 100)) };
const KIND_CHOICES = Object.entries(WEBSITE_COUPON_KIND).map(([id, { label }]) => ({ id, name: label }));
const ELIGIBILITY_CHOICES = Object.entries(WEBSITE_COUPON_ELIGIBILITY).map(([id, { label }]) => ({ id, name: label }));

const CouponFields = () => (
  <>
    <Box sx={layout.formGrid}>
      <TextInput source="code" label="קוד" fullWidth validate={required('שדה חובה')} inputProps={{ dir: 'ltr' }}
        parse={v => (v || '').toUpperCase()} helperText="אותיות באנגלית, ספרות, מקף או קו תחתון (3–40). פרטי — לא מוצג באתר." />
      <TextInput source="label" label="תיאור ללקוח (באנגלית)" fullWidth validate={required('שדה חובה')} inputProps={{ dir: 'ltr' }}
        helperText='מוצג ללקוח בסיכום ההזמנה, למשל "Free shipping".' />
      <SelectInput source="kind" label="סוג" choices={KIND_CHOICES} fullWidth validate={required('שדה חובה')} />
      <FormDataConsumer>
        {({ formData }) => (formData.kind === 'percent' ? (
          <NumberInput source="percent" label="אחוז הנחה" fullWidth min={1} max={100} validate={required('שדה חובה')}
            helperText="מהמוצרים בלבד, לא מהמשלוח" />
        ) : formData.kind === 'fixed' ? (
          <NumberInput source="amount_minor" label="סכום הנחה ($)" fullWidth {...usdInput} validate={required('שדה חובה')}
            helperText="מהמוצרים בלבד, לא יותר מסכום המוצרים" />
        ) : <Box />)}
      </FormDataConsumer>
      <NumberInput source="min_subtotal_minor" label="רק כשסכום המוצרים מעל ($)" fullWidth {...usdInput}
        helperText="ריק — בלי מינימום. לפני משלוח. לקופון השלוחים: 2000" />
      <SelectInput source="eligibility" label="מי יכול להשתמש" choices={ELIGIBILITY_CHOICES} fullWidth validate={required('שדה חובה')}
        helperText="שלוחים מאושרים — מסמנים בכרטיס הלקוח ב-CRM" />
      <DateTimeInput source="starts_at" label="בתוקף מ-" fullWidth helperText="ריק — מיד" />
      <DateTimeInput source="ends_at" label="בתוקף עד" fullWidth helperText="ריק — ללא תאריך סיום" />
      <NumberInput source="max_redemptions" label="מגבלת שימושים כוללת" fullWidth min={1} helperText="ריק — ללא הגבלה" />
      <NumberInput source="max_per_customer" label="מגבלת שימושים ללקוח" fullWidth min={1} helperText="ריק — ללא הגבלה" />
    </Box>
    <BooleanInput source="active" label="פעיל" />
    <TextInput source="notes" label="הערות פנימיות" fullWidth multiline minRows={2} />
  </>
);

// No delete: a coupon is deactivated, so orders that used it keep their history.
const SaveOnlyToolbar = () => <Toolbar><SaveButton label="שמירה" /></Toolbar>;

export const WebsiteCouponCreate = () => (
  <Create title="קופון חדש" redirect="list">
    <SimpleForm defaultValues={{ kind: 'percent', eligibility: 'everyone', active: true, notes: '' }}>
      <CouponFields />
    </SimpleForm>
  </Create>
);

// Pessimistic: the server checks `version` (409 when someone else saved first).
export const WebsiteCouponEdit = () => (
  <Edit title="עריכת קופון" mutationMode="pessimistic" redirect="list">
    <SimpleForm toolbar={<SaveOnlyToolbar />}>
      <CouponFields />
    </SimpleForm>
  </Edit>
);
